import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "../../publicAsset/userDashboardPage.module.scss";
import ScoreStatisticBoard from "../../components/scoreStatisticBoard/scoreStatisticBoard";
import ScoreSummary from "../../components/scoreSummary/scoreSummary";
import AccuracyTrend from "../../components/accuracyTrend/accuracyTrend";
import ExportPDF from "../../components/exportPDF/exportPDF";
import UserHeaderBar from "../../components/userHeaderBar/userHeaderBar";
import SubscriptionOverlay from "../../components/subscriptionOverlay/subscriptionOverlay";
import { monthlyAccuracyData$, monthlyAccuracyLoading$, dailyTrendLoading$ } from "../../reduxStore/selectors/recordSelectors";
import { usersData$ } from "../../reduxStore/selectors/userSelectors";
import { getMonthlySubjectAccuracy, getDailyAccuracyTrend } from "../../reduxStore/actions/recordActionsHelper";
import { isFeatureEnabled, FEATURES } from "../../utils/featureFlags";

const UserDashboardPage = () => {
    const dispatch = useDispatch();
    const [isExporting, setIsExporting] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState('all');
    const exportRef = useRef(null);
    const userData = useSelector(usersData$);
    const isLoadingMonthlyAccuracy = useSelector(monthlyAccuracyLoading$);
    const isLoadingDailyTrend = useSelector(dailyTrendLoading$);
    const accuracyData = useSelector(monthlyAccuracyData$);

    // Check subscription status
    const hasSubscription = userData?.subscription_end_date !== null &&
        userData?.subscription_end_date !== undefined;

    useEffect(() => {
        // Fetch monthly accuracy data and daily trend data
        if (userData?.id) {
            dispatch(getMonthlySubjectAccuracy.request({ userId: userData.id }));
            dispatch(getDailyAccuracyTrend.request({ userId: userData.id, subject: selectedSubject }));
        }
    }, [dispatch, userData?.id, selectedSubject]);

    const handleExportPDF = async () => {
        if (isExporting) return;

        setIsExporting(true);
        try {
            // Dynamically import the libraries
            const html2canvas = (await import('html2canvas')).default;
            const jsPDF = (await import('jspdf')).default;

            const element = exportRef.current;
            if (!element) return;

            // Capture the content as canvas
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 10;

            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
            pdf.save(`Prepal-Report-${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            alert('Failed to export PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>User Dashboard | Prepal</title>
                <meta name="description" content="User Dashboard for Prepal account holders." />
                <meta name="keywords" content="Prepal, user dashboard, education" />
                <meta name="author" content="Prepal Team" />
            </Helmet>
            <div className={styles.userDashboardPage}>
                <UserHeaderBar />
                <div style={{ position: 'relative' }}>
                    <div ref={exportRef}>
                        <div className={styles.scoreStatisticParent}>
                            <div className={styles.scoreStatisticMain}>
                                <ScoreStatisticBoard accuracyData={accuracyData} isLoading={isLoadingMonthlyAccuracy} />
                            </div>
                            <div className={styles.scoreStatisticSide}>
                                <ScoreSummary
                                    isLoading={isLoadingMonthlyAccuracy}
                                    totalAttempted={
                                        (accuracyData?.['Reading & Writing']?.attempted_s || 0) +
                                        (accuracyData?.['Math']?.attempted_s || 0)
                                    }
                                    overallAccuracy={
                                        (() => {
                                            const rwCorrect = accuracyData?.['Reading & Writing']?.correct_s || 0;
                                            const mathCorrect = accuracyData?.['Math']?.correct_s || 0;
                                            const rwAttempted = accuracyData?.['Reading & Writing']?.attempted_s || 0;
                                            const mathAttempted = accuracyData?.['Math']?.attempted_s || 0;
                                            const totalCorrect = rwCorrect + mathCorrect;
                                            const totalAttempted = rwAttempted + mathAttempted;
                                            return totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0;
                                        })()
                                    }
                                />
                            </div>
                        </div>
                        <div className={styles.scoreTrendParent}>
                            <div className={styles.scoreTrendMain}>
                                <AccuracyTrend
                                    isLoading={isLoadingDailyTrend}
                                    selectedSubject={selectedSubject}
                                    onSubjectChange={setSelectedSubject}
                                />
                            </div>
                            <div className={styles.scoreTrendSide}>
                                {isFeatureEnabled(FEATURES.EXPORT_PDF) && (
                                    <div onClick={handleExportPDF} style={{ cursor: isExporting ? 'wait' : 'pointer' }}>
                                        <ExportPDF isExporting={isExporting} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <SubscriptionOverlay isActive={!hasSubscription} positionType="absolute" />
                </div>
            </div>
        </>
    );
};

export default UserDashboardPage;
